import { customerRequest } from "./customerAuth";

export type CustomerCartDestination = "TABLE" | "GIFT" | "COUNTER";

let fallbackKeySequence = 0;

export interface CustomerCartSelection {
  menuExtraItemId: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  unitPriceCents: number;
  quantity: number;
}

export interface CustomerCartGroup {
  menuExtrasId: string;
  name: string;
  type: string;
  rule: string | null;
  minimum: number | null;
  maximum: number | null;
  exactly: number | null;
  selections: CustomerCartSelection[];
}

export interface CustomerCartItem {
  id: string;
  menuItemId: string;
  name: string;
  description: string;
  imageUrl: string | null;
  unitPriceCents: number;
  quantity: number;
  note: string | null;
  lineTotalCents: number;
  groups: CustomerCartGroup[];
}

export interface CustomerCartRestaurant {
  id: string;
  name: string;
  isOpen: boolean;
  serviceType: string | null;
  availableDestinations: CustomerCartDestination[];
  serviceFeeOptionsBasisPoints: number[];
}

export interface CustomerCartTotals {
  subtotalCents: number;
  serviceFeeCents: number;
  totalCents: number;
  totalUnits: number;
}

export interface CustomerCartConflict {
  type: string;
  cartItemId: string | null;
  message: string;
  resolution: string;
  previousCents?: number;
  currentCents?: number;
}

export interface CustomerCart {
  id: string | null;
  revision: number;
  expiresAt: string | null;
  restaurant: CustomerCartRestaurant | null;
  items: CustomerCartItem[];
  destination: CustomerCartDestination | null;
  tableNumber: string | null;
  serviceFeeBasisPoints: number | null;
  totals: CustomerCartTotals;
  conflicts: CustomerCartConflict[];
  readyForCheckout: boolean;
}

export interface CustomerCartSelectionInput {
  groupId: string;
  optionId: string;
  quantity: number;
}

export interface CustomerCartItemInput {
  menuItemId: string;
  quantity: number;
  note?: string | null;
  selections: CustomerCartSelectionInput[];
}

export interface CustomerCartPreferencesInput {
  destination: CustomerCartDestination;
  tableNumber?: string | null;
  serviceFeeBasisPoints?: number | null;
}

export interface CustomerCartMutationMeta {
  revision: number;
  idempotencyKey: string;
}

function mutationHeaders(meta: CustomerCartMutationMeta): Record<string, string> {
  return {
    "If-Match": `"${meta.revision}"`,
    "Idempotency-Key": meta.idempotencyKey,
  };
}

export function createCustomerCartIdempotencyKey(): string {
  const cryptoProvider = globalThis.crypto as (Crypto & { randomUUID?: () => string }) | undefined;
  const randomUuid = cryptoProvider?.randomUUID?.();
  let entropy = randomUuid;
  if (!entropy && cryptoProvider) {
    const randomBytes = cryptoProvider.getRandomValues(new Uint8Array(16));
    entropy = Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  if (!entropy) entropy = `fallback_${fallbackKeySequence++}`;
  const key = `${Date.now()}_${entropy}`.replace(/[^A-Za-z0-9_-]/g, "");

  return `cart_${key}`.slice(0, 128).padEnd(16, "0");
}

export const customerCartApi = {
  getCurrent: () => customerRequest<CustomerCart>({ method: "GET", url: "customer/cart" }),
  addItem: (input: CustomerCartItemInput, meta: CustomerCartMutationMeta) =>
    customerRequest<CustomerCart>({
      method: "POST",
      url: "customer/cart/items",
      data: input,
      headers: mutationHeaders(meta),
    }),
  replaceItem: (cartItemId: string, input: CustomerCartItemInput, meta: CustomerCartMutationMeta) =>
    customerRequest<CustomerCart>({
      method: "PATCH",
      url: `customer/cart/items/${encodeURIComponent(cartItemId)}`,
      data: input,
      headers: mutationHeaders(meta),
    }),
  removeItem: (cartItemId: string, meta: CustomerCartMutationMeta) =>
    customerRequest<CustomerCart>({
      method: "DELETE",
      url: `customer/cart/items/${encodeURIComponent(cartItemId)}`,
      headers: mutationHeaders(meta),
    }),
  clear: (meta: CustomerCartMutationMeta) =>
    customerRequest<CustomerCart>({
      method: "DELETE",
      url: "customer/cart",
      headers: mutationHeaders(meta),
    }),
  updatePreferences: (input: CustomerCartPreferencesInput, meta: CustomerCartMutationMeta) =>
    customerRequest<CustomerCart>({
      method: "PATCH",
      url: "customer/cart/preferences",
      data: input,
      headers: mutationHeaders(meta),
    }),
  acceptPrice: (cartItemId: string, meta: CustomerCartMutationMeta) =>
    customerRequest<CustomerCart>({
      method: "POST",
      url: `customer/cart/items/${encodeURIComponent(cartItemId)}/accept-price`,
      headers: mutationHeaders(meta),
    }),
};
